import Link from "next/link";
import { cn } from "@/lib/utils";

/**
 * The consent line that sits above every enquiry form's submit button.
 *
 * One component rather than a sentence pasted into three forms, because the
 * three had already drifted: `/dedicated` and `/engineers` each carried a
 * hand-written line promising "our privacy policy" at a time when no privacy
 * policy existed to link to, and the early-access modal said nothing at all.
 * Promising a document you do not have is the worst of the three states.
 *
 * **A notice, not a tick box, and that is deliberate.** These forms exist to
 * answer the person filling them in; replying to an enquiry is the reason the
 * details were given, so a checkbox saying "yes, reply to me" is friction that
 * protects nobody.
 *
 * Product updates are a different purpose and have their own tick box,
 * `UpdatesOptIn`, which the early-access form carries (2026-09-22). This line
 * used to promise "we will not add you to a mailing list"; it was removed on
 * instruction when the updates list started, because a blanket promise here
 * would contradict the box beside it. Whether someone is on the list is the
 * box's answer, not this sentence's.
 */
export function FormConsent({ className }: { className?: string }) {
  return (
    <p className={cn("font-sans text-xs leading-relaxed text-muted-2", className)}>
      By sending this you agree we may use these details to reply to you. We will not sell them or
      share them for anyone else&rsquo;s marketing. What we do with them is set out in the{" "}
      <Link
        href="/privacy"
        className="underline decoration-border-strong underline-offset-2 transition-colors hover:text-foreground"
      >
        privacy policy
      </Link>
      .
    </p>
  );
}
